import React, { useEffect, useState } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonButton,
  IonIcon,
  IonInput,
  IonModal,
  IonFooter,
  IonText,
  IonSpinner,
} from '@ionic/react';
import { addOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { apiUrl, apiHeaders } from '../lib/api';
import { useI18n } from '../i18n/I18nProvider';

interface TopicSummary {
  id: string;
  name: string;
  description: string | null;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  role: string | null;
}

const TopicsPage: React.FC = () => {
  const history = useHistory();
  const { getToken } = useAuth();
  const { t } = useI18n();

  const [topics, setTopics] = useState<TopicSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [newTopicDescription, setNewTopicDescription] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadTopics() {
      try {
        setLoading(true);
        setError(null);

        const token = await getToken();
        const res = await fetch(apiUrl('/api/chat/topics'), {
          headers: {
            'Content-Type': 'application/json',
            ...apiHeaders(token),
          },
        });

        if (!res.ok) {
          throw new Error(`Failed to load topics (${res.status})`);
        }

        const data = (await res.json()) as TopicSummary[];
        if (isMounted) {
          setTopics(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : t('topics.error.loadFailed'));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadTopics();

    return () => {
      isMounted = false;
    };
  }, [getToken, t]);

  const handleOpenTopic = (topicId: string) => {
    history.push(`/topics/${topicId}/conversations`);
  };

  const handleCreateTopic = async () => {
    if (!newTopicName.trim()) {
      setError(t('topics.error.nameRequired'));
      return;
    }

    try {
      setCreating(true);
      setError(null);
      const token = await getToken();

      const res = await fetch(apiUrl('/api/chat/topics'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...apiHeaders(token),
        },
        body: JSON.stringify({
          name: newTopicName.trim(),
          description: newTopicDescription.trim() || undefined,
          isPublic: true,
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => undefined);
        const msg =
          body?.message || `${t('topics.error.createFailed')} (${res.status})`;
        throw new Error(msg);
      }

      const created = (await res.json()) as TopicSummary;
      setTopics((prev) => [created, ...prev]);
      setShowCreateModal(false);
      setNewTopicName('');
      setNewTopicDescription('');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t('topics.error.createFailed')
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              defaultHref="/dashboard"
              text={t('common.actions.back')}
            />
          </IonButtons>
          <IonTitle>{t('topics.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => setShowCreateModal(true)}>
              <IonIcon slot="icon-only" icon={addOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="ion-padding vault-screen">
        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && !loading && (
          <IonText color="danger">
            <p>{error}</p>
          </IonText>
        )}

        {!loading && !error && topics.length === 0 && (
          <IonText color="medium">
            <p>{t('topics.empty')}</p>
          </IonText>
        )}

        <IonList className="vault-feed-list">
          {topics.map((topic) => (
            <IonItem
              key={topic.id}
              button
              detail
              lines="none"
              className="vault-feed-item"
              onClick={() => handleOpenTopic(topic.id)}
            >
              <IonLabel>
                <h2>{topic.name}</h2>
                {topic.description && <p>{topic.description}</p>}
                {topic.role && (
                  <p>
                    <small>{t('topics.modal.role', { role: topic.role })}</small>
                  </p>
                )}
              </IonLabel>
            </IonItem>
          ))}
        </IonList>
      </IonContent>

      <IonModal
        className="vault-glass-modal"
        isOpen={showCreateModal}
        onDidDismiss={() => setShowCreateModal(false)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('topics.modal.newTopic.title')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonItem lines="none" className="vault-field-item">
            <IonLabel position="stacked">
              {t('topics.modal.name.label')}
            </IonLabel>
            <IonInput
              value={newTopicName}
              onIonChange={(e) => setNewTopicName(e.detail.value ?? '')}
              placeholder={t('topics.modal.name.placeholder')}
            />
          </IonItem>
          <IonItem lines="none" className="vault-field-item">
            <IonLabel position="stacked">
              {t('topics.modal.description.label')}
            </IonLabel>
            <IonInput
              value={newTopicDescription}
              onIonChange={(e) => setNewTopicDescription(e.detail.value ?? '')}
              placeholder={t('topics.modal.description.placeholder')}
            />
          </IonItem>
          {error && (
            <IonText color="danger">
              <p>{error}</p>
            </IonText>
          )}
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setShowCreateModal(false)}>
                {t('topics.modal.actions.cancel')}
              </IonButton>
            </IonButtons>
            <IonButtons slot="end">
              <IonButton onClick={handleCreateTopic} disabled={creating}>
                {creating ? (
                  <IonSpinner name="crescent" />
                ) : (
                  t('topics.modal.actions.create')
                )}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </IonModal>
    </IonPage>
  );
};

export default TopicsPage;
